import React, { useContext, useEffect, useRef, useState } from "react";
import { apiGet } from "../../utils/apiFetch";
import Pagination from "../Pagination";
import dayjs from "dayjs";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import apiPath from "utils/apiPath";
import Table from "./Table";
import { BiReset } from "react-icons/bi";
import CategoryImageModal from "./CategoryImageModal";
import AnimatedComponent from "components/reusable/AnimatedComponent";
import helpers from "utils/helpers";
import OSearch from "components/reusable/OSearch";

function AnnouncementManager() {
  const { t } = useTranslation();
  const childRef = useRef();
  const { user, updatePageName } = useContext(AuthContext);
  const [imageShowModal, setImageShowModal] = useState(false);
  const manager =
    user?.permission?.find((e) => e.manager === "service-manager") ?? {};
  const [announcements, setAnnouncements] = useState();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isDelete, setIsDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [filterData, setFilterData] = useState({
    searchkey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortKey: "createdAt",
    sortType: "desc",
  });

  useEffect(() => {
    updatePageName(t("ANNOUNCEMENT_MANAGER"));
  }, []);

  const getAllAnnouncements = async (data) => {
    try {
      const { startDate, endDate, isFilter, searchkey } = filterData;

      if (
        (data?.deletePage && announcements?.length >= 1) ||
        (isFilter &&
          announcements &&
          data?.statusChange &&
          announcements?.length >= 1)
      ) {
        setPage(page - 1);
        setIsDelete(true);
        setPaginationObj({ ...paginationObj, page: page - 1 });
      } else {
        setIsDelete(false);
      }

      const payload = {
        page,
        pageSize: pageSize,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
        sortKey: sort.sortKey,
        sortType: sort.sortType,
        keyword: helpers.ternaryCondition(
          searchkey?.includes("+"),
          searchkey?.split("-")?.[1],
          searchkey
        ),
      };

      const path = apiPath.announcementList;
      const result = await apiGet(path, payload);
      const response = result?.data?.results;
      const resultStatus = result?.data?.success;
      if (data?.type == "download") {
        helpers.downloadFile(response?.csvFilePath);
        return;
      }

      setAnnouncements(response);
      setPaginationObj({
        ...paginationObj,
        page: resultStatus ? response.page : null,
        pageCount: resultStatus ? response.totalPages : null,
        perPageItem: resultStatus ? response?.docs.length : null,
        totalItems: resultStatus ? response.totalDocs : null,
      });
    } catch (error) {
      console.error("error in get all list==>>>>", error.message);
    }
  };
  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  useEffect(() => {
    getAllAnnouncements(filterData);
  }, [filterData, page, sort]);

  const handleReset = () => {
    setSearchTerm("");
    setFilterData({
      searchkey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
    childRef?.current?.onClose();
  };

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      isReset: false,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
  };

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    } else if (searchTerm || !filterData?.isReset) {
      setFilterData({
        ...filterData,
        isReset: false,
        searchkey: debouncedSearchTerm ? debouncedSearchTerm : "",
        isFilter: debouncedSearchTerm ? true : false,
      });
      setPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  return (
    <div>
      <div className="bg-[#F9F9F9] dark:bg-LightBlue">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] rounded-lg dark:bg-slate-800">
            {/* filter section */}
            <form className="border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3 flex-wrap justify-between">
              <div className="col-span-2 flex flex-wrap  items-center">
                <div className="flex items-center lg:pt-0 pt-3 flex-wrap justify-center mb-2 2xl:mb-0">
                  <div className="relative flex items-center mb-3">
                    <OSearch
                      placeholders={t("SEARCH_BY_NAME_DESCRIPTION")}
                      onChange={(e) => {
                        setSearchTerm(e?.target?.value);
                      }}
                      className="w-[300px] h-[38px] rounded-lg"
                      ref={childRef}
                    />
                  </div>
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    isReset={filterData?.isReset}
                    setIsReset={setFilterData}
                  />
                  <button
                    title={t("O_RESET")}
                    type="button"
                    onClick={handleReset}
                    className="bg-gradientTo text-sm px-6 flex gap-2 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2"
                  >
                    <BiReset size={18} /> {t("O_RESET")}
                  </button>
                </div>
              </div>
            </form>

            <AnimatedComponent>
              <Table
                announcements={announcements?.docs}
                getAllAnnouncements={getAllAnnouncements}
                page={page}
                setSort={setSort}
                sort={sort}
              />
            </AnimatedComponent>
            <AnimatedComponent>
              {paginationObj?.totalItems ? (
                <Pagination
                  handlePageClick={handlePageClick}
                  options={paginationObj}
                  isDelete={isDelete}
                  page={page}
                />
              ) : null}
            </AnimatedComponent>
          </div>
        </div>
      </div>

      {imageShowModal && (
        <CategoryImageModal
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          closeImageModal={setImageShowModal}
        />
      )}
    </div>
  );
}

export default AnnouncementManager;
